import { FC, useMemo, useEffect } from 'react';
import {
  FieldsSection,
  FormSelect,
  SelectMenuWithFooter,
} from '../../../../../../shared/components/form';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useLoaderData } from 'react-router-dom';
import { ITabState } from '../../../states/tabState.model.ts';
import { Stack, Typography } from '@mui/material';
import { notificationCenter } from '../../../../../../shared/notificationCenter/notificationCenter.ts';
import {
  printFormsService,
  SaveReportPrinterMappingsMutationRes,
} from '../../../services/printForms.service.ts';
import {
  C_Printed_Reports_Kind,
  C_Save_Operation_Status,
} from '../../../../../../graphql/generatedModel.ts';
import { take } from 'rxjs';
import { responseHandler } from '../../../../../../shared/responseHandler/responseHandler.ts';
import { snackbarService } from '../../../../../../shared/components/snackbar/service/snackbar.service.ts';
import { ISelectOption } from '../../../../../../shared/components/form/fields/select.component.tsx';
import { styled } from '@mui/material/styles';
import { DictCompanyPrintersQueryRes } from '../../../../../../shared/services/configsData/configsData.service.ts';
import { statusCircle } from '../../../../../../shared/components/virtualization/virtualization.component.tsx';
import { grey } from '@mui/material/colors';

export const PrintersAssignment: FC = () => {
  const { t } = useTranslation();
  const { companyPrinters, listReportPrinterMappings } = useLoaderData() as ITabState;
  const order = [
    C_Printed_Reports_Kind.BAKING_LISTS,
    C_Printed_Reports_Kind.FORWARDING_LISTS,
    C_Printed_Reports_Kind.CROSSTABLE_REPORTS,
    C_Printed_Reports_Kind.OFFERS,
    C_Printed_Reports_Kind.DELIVERY_NOTES_A4,
    C_Printed_Reports_Kind.DELIVERY_NOTES_A5,
    C_Printed_Reports_Kind.INVOICES,
    C_Printed_Reports_Kind.INVOICE_PAYMENT_SLIP,
    C_Printed_Reports_Kind.INVOICE_SUMMARY,
  ];
  const listReportPrinterMappingsSorted = useMemo(() => {
    return listReportPrinterMappings.sort(
      (a, b) => order.indexOf(a.printedReportsKindId) - order.indexOf(b.printedReportsKindId),
    );
  }, [listReportPrinterMappings]);

  const maxWidth = useMemo(() => {
    return (
      Math.max(
        ...listReportPrinterMappingsSorted.map(
          (item) => t(`settings.${item.printedReportsKindId}`).length,
        ),
      ) * 9
    );
  }, [t]);

  const formMethods = useForm({
    defaultValues: Object.fromEntries(
      listReportPrinterMappingsSorted.map(({ printedReportsKindId, printerId }) => [
        printedReportsKindId,
        printerId,
      ]),
    ),
  });

  useEffect(() => {
    const watchSub = formMethods.watch((fields, { type, name }) => {
      if (type === 'change') {
        notificationCenter.notify(printFormsService.sub.saveReportPrinterMappings(), {
          type: undefined,
          handler: (stream) => {
            stream.pipe(take(1), responseHandler()).subscribe((res) => {
              const typedRes = res as SaveReportPrinterMappingsMutationRes;
              if (typedRes?.status === C_Save_Operation_Status.SOS1_DATA_CHANGED) {
                snackbarService.pub.show({
                  type: 'success',
                  content: t('settings.configuration_saved'),
                });
              } else {
                snackbarService.pub.show({
                  type: 'error',
                  content: t('settings.configuration_not_saved'),
                });
              }
            });
          },
        });

        const printedReportsKindId = name as C_Printed_Reports_Kind;
        const printerId = fields[name as keyof typeof fields];
        printFormsService.pub.saveReportPrinterMappings({
          mapping: [{ printedReportsKindId, printerId }],
        });
      }
    });
    return () => watchSub.unsubscribe();
  }, []);

  return (
    <FieldsSection titleBoxSx={{ pt: 0 }} nomt title={t('settings.form_printing_assignment')}>
      <FormProvider {...formMethods}>
        <Stack
          sx={{
            gap: 3,
          }}
        >
          {listReportPrinterMappingsSorted.map(({ printedReportsKindId }, index) => (
            <Stack
              key={index}
              direction='row'
              sx={{
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  width: maxWidth,
                }}
              >
                {t(`settings.${printedReportsKindId}`)}
              </Typography>
              <SFormSelect
                selected={!!formMethods.watch(printedReportsKindId)}
                datasetattribute='companyPrintersOptions'
                optionData={companyPrinters.find(
                  (el) => el.id === formMethods.watch(printedReportsKindId),
                )}
                filterOptions={(opts) =>
                  opts.filter((option) => {
                    if (!(option as ExtendedSelectOption).isDeactivated) {
                      return true;
                    }
                  })
                }
                PaperComponent={(props) => (
                  <SelectMenuWithFooter
                    {...props}
                    href='https://console.hssoft.com/console/company/wawiassist/print-node/printers'
                  />
                )}
                width='350px'
                label={t('common.printer')}
                name={printedReportsKindId}
                options={companyPrinters as []}
              />
            </Stack>
          ))}
        </Stack>
      </FormProvider>
    </FieldsSection>
  );
};

interface ExtendedSelectOption extends ISelectOption {
  isDeactivated: boolean;
}

const SFormSelect = styled(FormSelect, {
  shouldForwardProp: (prop) => prop !== 'optionData' && prop !== 'selected',
})<{
  optionData: DictCompanyPrintersQueryRes[number] | undefined;
  selected: boolean;
}>(({ theme, optionData, selected }) => ({
  '& input': {
    color: optionData?.isDeactivated ? theme.palette.text.disabled : theme.palette.text.primary,
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    alignItems: 'baseline',
  },
  ...(selected && {
    '& .MuiInputBase-root.MuiOutlinedInput-root::before': {
      content: '""',
      ...statusCircle(
        !!optionData?.printerAvailable,
        optionData?.isDeactivated ? grey['300'] : undefined,
      ),
      marginLeft: 5,
      marginRight: 0,
    },
  }),
}));
